import React, { useCallback, useEffect, useState, useMemo } from "react";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RcTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

import TimerIcon from "@material-ui/icons/Timer";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import HourglassIcon from "@material-ui/icons/HourglassEmpty";
import FunnelIcon from "@material-ui/icons/FilterList";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import KpiCard from "../../components/KpiCard";
import { categorical, marks, chartTheme, status } from "../../theme/charts";

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const fmtNum = (v) => Number(v || 0).toLocaleString("pt-BR");

const fmtMonth = (ym) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-");
  const meses = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${meses[Number(m) - 1]}/${String(y).slice(2)}`;
};

const primeiroDiaDoMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

const useStyles = makeStyles((theme) => ({
  body: { flex: 1, minHeight: 0, overflow: "auto", paddingTop: theme.spacing(2) },
  tabs: { flexBasis: "100%", minHeight: 40, marginBottom: theme.spacing(-0.5) },
  card: { padding: theme.spacing(2), marginTop: theme.spacing(2) },
  cardTitle: { fontWeight: 600 },
  cardHint: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 2 },
  chartBox: { height: 280, marginTop: theme.spacing(1.5) },
  num: { fontVariantNumeric: "tabular-nums" },
  money: { fontVariantNumeric: "tabular-nums", fontWeight: 600 },
  muted: { color: theme.palette.text.hint },
  stageBar: {
    height: 8,
    borderRadius: 4,
    background: theme.palette.action.hover,
    overflow: "hidden",
    marginTop: 4,
  },
  stageFill: { height: "100%", borderRadius: 4 },
}));

/**
 * Painel comercial.
 *
 * Separado do dashboard geral porque as perguntas são outras: lá é "como está
 * o negócio hoje", aqui é "onde o funil trava e por que se perde". Misturar as
 * duas produz uma tela longa em que nenhuma das duas se responde bem.
 */
const SalesAnalytics = () => {
  const classes = useStyles();
  const muiTheme = useTheme();
  const ct = useMemo(() => chartTheme(muiTheme), [muiTheme]);

  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState({
    start: primeiroDiaDoMes(),
    end: new Date().toISOString().slice(0, 10),
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/analytics/sales", { params: period });
      setData(d);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const c = data?.cycle;
  const stages = data?.stages || [];
  const maiorEtapa = Math.max(1, ...stages.map((s) => s.amount || 0));

  const stageColumns = [
    {
      key: "stageName",
      label: "Etapa",
      render: (s) => (
        <div>
          <Typography variant="subtitle2">{s.stageName}</Typography>
          {/* Barra proporcional ao valor: mostra onde o dinheiro está parado,
              que é diferente de onde estão os negócios. */}
          <div className={classes.stageBar}>
            <div
              className={classes.stageFill}
              style={{
                width: `${((s.amount || 0) / maiorEtapa) * 100}%`,
                background: categorical[0],
              }}
            />
          </div>
        </div>
      ),
    },
    { key: "count", label: "Negócios", align: "right", render: (s) => <span className={classes.num}>{fmtNum(s.count)}</span> },
    { key: "amount", label: "Valor", align: "right", render: (s) => <span className={classes.money}>{fmtMoney(s.amount)}</span> },
    { key: "probability", label: "Probabilidade", align: "right", render: (s) => <span className={classes.num}>{s.probability}%</span> },
    {
      key: "weighted",
      label: "Ponderado",
      align: "right",
      render: (s) => <span className={classes.money}>{fmtMoney(s.weighted)}</span>,
    },
    {
      key: "avgAge",
      label: "Idade média",
      align: "right",
      render: (s) =>
        s.count > 0 ? (
          <span className={classes.num}>{s.avgAge} dias</span>
        ) : (
          <span className={classes.muted}>—</span>
        ),
    },
  ];

  const ownerColumns = [
    { key: "ownerName", label: "Vendedor", render: (o) => <Typography variant="subtitle2">{o.ownerName}</Typography> },
    { key: "won", label: "Ganhos", align: "right", render: (o) => <span className={classes.num}>{fmtNum(o.won)}</span> },
    { key: "lost", label: "Perdidos", align: "right", render: (o) => <span className={classes.num}>{fmtNum(o.lost)}</span> },
    { key: "open", label: "Em aberto", align: "right", render: (o) => <span className={classes.num}>{fmtNum(o.open)}</span> },
    { key: "winRate", label: "Taxa de vitória", align: "right", render: (o) => <span className={classes.num}>{o.winRate}%</span> },
    { key: "wonAmount", label: "Valor ganho", align: "right", render: (o) => <span className={classes.money}>{fmtMoney(o.wonAmount)}</span> },
    {
      key: "avgDaysToClose",
      label: "Ciclo médio",
      align: "right",
      render: (o) =>
        o.avgDaysToClose != null ? (
          <span className={classes.num}>{o.avgDaysToClose} dias</span>
        ) : (
          <span className={classes.muted}>—</span>
        ),
    },
  ];

  const sourceColumns = [
    { key: "sourceName", label: "Origem", render: (s) => <Typography variant="subtitle2">{s.sourceName}</Typography> },
    { key: "deals", label: "Negócios", align: "right", render: (s) => <span className={classes.num}>{fmtNum(s.deals)}</span> },
    { key: "won", label: "Fecharam", align: "right", render: (s) => <span className={classes.num}>{fmtNum(s.won)}</span> },
    { key: "winRate", label: "Taxa", align: "right", render: (s) => <span className={classes.num}>{s.winRate}%</span> },
    { key: "wonAmount", label: "Valor ganho", align: "right", render: (s) => <span className={classes.money}>{fmtMoney(s.wonAmount)}</span> },
  ];

  const lossColumns = [
    { key: "reason", label: "Motivo", render: (l) => <Typography variant="subtitle2">{l.reason}</Typography> },
    { key: "count", label: "Negócios", align: "right", render: (l) => <span className={classes.num}>{fmtNum(l.count)}</span> },
    {
      key: "amount",
      label: "Valor perdido",
      align: "right",
      render: (l) => <span className={classes.money}>{fmtMoney(l.amount)}</span>,
    },
  ];

  const eixoComum = {
    tick: ct.axisTick,
    tickLine: false,
  };

  return (
    <MainContainer>
      <PageHeader
        title="Análise Comercial"
        subtitle="Onde o funil trava, quanto tempo leva para fechar e por que se perde."
        filters={
          <>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
            >
              <Tab label="Visão geral" />
              <Tab label="Funil" />
              <Tab label="Prospecção" />
              <Tab label="Equipe" />
            </Tabs>
            <TextField
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={period.start}
              onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
            />
            <TextField
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={period.end}
              onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
            />
          </>
        }
      />

      <div className={classes.body}>
        {/* --- Visão geral ------------------------------------------------ */}
        {tab === 0 && c && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <KpiCard
                  label="Taxa de vitória"
                  value={`${c.winRate}%`}
                  icon={EmojiEventsIcon}
                  hint={`${fmtNum(c.wonCount)} ganhos, ${fmtNum(c.lostCount)} perdidos`}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard
                  label="Ciclo de vendas"
                  value={c.avgDaysToClose != null ? `${c.avgDaysToClose} dias` : "—"}
                  icon={TimerIcon}
                  hint={
                    c.fastestDays != null
                      ? `entre ${c.fastestDays} e ${c.slowestDays} dias`
                      : "sem negócio fechado no período"
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard
                  label="Funil em aberto"
                  value={fmtMoney(c.openAmount)}
                  icon={FunnelIcon}
                  hint={`${fmtNum(c.openCount)} negócios`}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <KpiCard
                  label="Idade média do funil"
                  value={c.avgOpenAge != null ? `${c.avgOpenAge} dias` : "—"}
                  icon={HourglassIcon}
                  hint={
                    c.oldestOpenAge != null
                      ? `o mais antigo tem ${c.oldestOpenAge} dias`
                      : undefined
                  }
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                Ganhos nos últimos 12 meses
              </Typography>
              <div className={classes.cardHint}>
                Quantidade e valor na mesma escala de tempo, em gráficos separados —
                duas escalas num eixo só distorcem a leitura.
              </div>
              <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={(data.monthly || []).map((m) => ({ ...m, mes: fmtMonth(m.month) }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={ct.grid} />
                    <XAxis dataKey="mes" {...eixoComum} axisLine={{ stroke: ct.grid }} />
                    <YAxis {...eixoComum} axisLine={false} width={70} tickFormatter={fmtMoney} />
                    <RcTooltip
                      contentStyle={ct.tooltip}
                      formatter={(v, n) => (n === "Valor" ? fmtMoney(v) : fmtNum(v))}
                    />
                    <Legend iconType="plainline" />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      name="Valor"
                      stroke={categorical[0]}
                      strokeWidth={marks.lineWidth}
                      dot={{ r: marks.dotRadius }}
                      activeDot={{ r: marks.activeDotRadius }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Paper>

            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                Previsão pelos próximos 6 meses
              </Typography>
              <div className={classes.cardHint}>
                Soma dos negócios abertos com fechamento esperado em cada mês. Não é
                projeção estatística — é o que o time declarou, ponderado pela
                probabilidade da etapa.
              </div>
              <div className={classes.chartBox}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(data.forecast || []).map((m) => ({ ...m, mes: fmtMonth(m.month) }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={ct.grid} />
                    <XAxis dataKey="mes" {...eixoComum} axisLine={{ stroke: ct.grid }} />
                    <YAxis {...eixoComum} axisLine={false} width={70} tickFormatter={fmtMoney} />
                    <RcTooltip contentStyle={ct.tooltip} formatter={(v) => fmtMoney(v)} />
                    <Legend />
                    <Bar dataKey="amount" name="Declarado" fill={categorical[0]} radius={marks.barRadius} />
                    <Bar dataKey="weighted" name="Ponderado" fill={categorical[2]} radius={marks.barRadius} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </>
        )}

        {/* --- Funil ------------------------------------------------------- */}
        {tab === 1 && (
          <>
            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                Onde estão os negócios abertos
              </Typography>
              <div className={classes.cardHint}>
                A etapa com mais dinheiro parado nem sempre é a com mais negócios — e
                a idade média mostra qual delas está travando.
              </div>
            </Paper>
            <DataTable
              columns={stageColumns}
              rows={stages}
              loading={loading}
              getRowKey={(s) => s.stageId}
              emptyIcon={<FunnelIcon />}
              emptyTitle="Nenhuma etapa configurada"
              emptyDescription="Configure as etapas do pipeline para acompanhar onde os negócios param."
            />
          </>
        )}

        {/* --- Prospecção -------------------------------------------------- */}
        {tab === 2 && (
          <>
            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                De onde vêm os negócios
              </Typography>
              <div className={classes.cardHint}>
                Origem cadastrada no cliente. Muitos em &quot;Sem origem&quot; significa que a
                atribuição não está sendo preenchida — e aí não há o que comparar.
              </div>
            </Paper>
            <DataTable
              columns={sourceColumns}
              rows={data?.sources || []}
              loading={loading}
              getRowKey={(s) => s.sourceId || s.sourceName}
              emptyTitle="Nenhum negócio no período"
              emptyDescription="Sem negócios criados no recorte escolhido não há origem a comparar."
            />

            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                Por que perdemos
              </Typography>
              <div className={classes.cardHint}>
                Ordenado por valor perdido, e não por quantidade: dez negócios
                pequenos doem menos que um grande.
              </div>
              {(data?.losses || []).length > 0 && (
                <div className={classes.chartBox}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.losses} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={ct.grid} />
                      <XAxis type="number" {...eixoComum} axisLine={false} tickFormatter={fmtMoney} />
                      <YAxis
                        type="category"
                        dataKey="reason"
                        {...eixoComum}
                        axisLine={false}
                        width={160}
                      />
                      <RcTooltip contentStyle={ct.tooltip} formatter={(v) => fmtMoney(v)} />
                      <Bar dataKey="amount" name="Valor perdido" radius={[0, 4, 4, 0]}>
                        {data.losses.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? status.critical : categorical[4]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Paper>
            <DataTable
              columns={lossColumns}
              rows={data?.losses || []}
              loading={loading}
              getRowKey={(l) => l.reasonId || l.reason}
              emptyTitle="Nenhuma perda no período"
              emptyDescription="Registre o motivo ao marcar um negócio como perdido — é o que permite atacar a causa em vez de adivinhar."
            />
          </>
        )}

        {/* --- Equipe ------------------------------------------------------ */}
        {tab === 3 && (
          <>
            <Paper variant="outlined" className={classes.card}>
              <Typography variant="subtitle1" className={classes.cardTitle}>
                Desempenho por vendedor
              </Typography>
              <div className={classes.cardHint}>
                Taxa de vitória é sobre o que foi decidido, não sobre tudo que existe:
                incluir os abertos puniria justamente quem está prospectando mais.
              </div>
            </Paper>
            <DataTable
              columns={ownerColumns}
              rows={data?.owners || []}
              loading={loading}
              getRowKey={(o) => o.ownerId || o.ownerName}
              emptyTitle="Nenhum negócio atribuído"
              emptyDescription="Atribua responsável aos negócios para acompanhar o desempenho de cada um."
            />
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default SalesAnalytics;
